import { Input, Select } from '@/components/ui/FormControls';
import { parseCalendarDate } from '@klothiq/domain';
// ─────────────────────────────────────────────
// KLOTHIQ — Plans List Page
// ─────────────────────────────────────────────

import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  Calendar,
  Target,
  CheckCircle2,
  AlertTriangle,
  Archive,
  ArrowUpRight,
  Edit2,
  Layers,
  Clock,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { usePlansList, useArchivePlan, type PlanWithMetrics } from '@/hooks/usePlans';
import PlanFormModal from './PlanFormModal';
import ErrorState from '@/components/common/ErrorState';
import { useWorkIntent } from '@/hooks/useWorkIntent';
import { workCompletionPercent } from '@klothiq/domain';

export default function PlansListPage() {
  const navigate = useNavigate();
  const { can } = useAuth();
  const canManage = can('plans.manage');

  const [activeTab, setActiveTab] = useState<'active' | 'archived'>('active');
  const [search, setSearch] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<PlanWithMetrics | null>(null);

  const {
    data: plans = [],
    isLoading,
    isError,
    error,
    refetch,
  } = usePlansList({
    archiveState: activeTab,
    search,
    priority: priorityFilter,
  });

  const archivePlan = useArchivePlan();
  const archiveIntent = useWorkIntent();

  // Aggregate metrics
  const metrics = useMemo(() => {
    const total = plans.length;
    const totalTasks = plans.reduce((acc, p) => acc + p.total_tasks, 0);
    const completedTasks = plans.reduce((acc, p) => acc + p.completed_tasks, 0);
    const blockedTasks = plans.reduce((acc, p) => acc + p.blocked_tasks, 0);
    const overduePlans = plans.filter((p) => p.is_overdue).length;
    const avgProgress = workCompletionPercent(completedTasks, totalTasks);

    return { total, totalTasks, completedTasks, blockedTasks, overduePlans, avgProgress };
  }, [plans]);

  const handleEdit = (plan: PlanWithMetrics, e: React.MouseEvent) => {
    e.stopPropagation();
    if (archivePlan.isPending) return;
    setEditingPlan(plan);
    setIsModalOpen(true);
  };

  const handleArchiveToggle = async (plan: PlanWithMetrics, e: React.MouseEvent) => {
    e.stopPropagation();
    if (archivePlan.isPending) return;
    if (
      window.confirm(
        plan.is_archived
          ? `Unarchive plan "${plan.title}"?`
          : `Archive plan "${plan.title}"? Active tasks will be preserved.`,
      )
    ) {
      const input = {
        plan_id: plan.id,
        expected_version: plan.version,
        archived: !plan.is_archived,
      };
      try {
        await archivePlan.mutateAsync({ ...input, request_key: archiveIntent.key(plan.id, input) });
        archiveIntent.complete(plan.id);
      } catch {
        /* Error toast is shown by the mutation. */
      }
    }
  };

  return (
    <div className="flex-1 space-y-6 p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto overflow-y-auto">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">Plans & Projects</h1>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1">
            Organize milestones, coordinate team tasks, and monitor delivery progress.
          </p>
        </div>

        {canManage && (
          <button
            onClick={() => {
              setEditingPlan(null);
              setIsModalOpen(true);
            }}
            className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-xs sm:text-sm font-semibold text-primary-foreground shadow hover:bg-primary/90 transition-all self-start sm:self-auto"
          >
            <Plus className="h-4 w-4" />
            <span>New Plan</span>
          </button>
        )}
      </div>

      {/* Metrics Ribbon */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <div className="rounded-xl border border-border bg-card/60 p-4 backdrop-blur-sm">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-muted-foreground">Total Plans</span>
            <Layers className="h-4 w-4 text-primary" />
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-2xl font-bold text-foreground">{metrics.total}</span>
            <span className="text-[11px] text-muted-foreground">
              {activeTab === 'active' ? 'Active' : 'Archived'}
            </span>
          </div>
        </div>

        <div className="rounded-xl border border-border bg-card/60 p-4 backdrop-blur-sm">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-muted-foreground">Overall Progress</span>
            <CheckCircle2 className="h-4 w-4 text-emerald-500" />
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-2xl font-bold text-foreground">{metrics.avgProgress}%</span>
            <span className="text-[11px] text-muted-foreground">
              {metrics.completedTasks}/{metrics.totalTasks} Tasks
            </span>
          </div>
        </div>

        <div className="rounded-xl border border-border bg-card/60 p-4 backdrop-blur-sm">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-muted-foreground">Blocked Tasks</span>
            <AlertTriangle className="h-4 w-4 text-amber-500" />
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-2xl font-bold text-foreground">{metrics.blockedTasks}</span>
            <span className="text-[11px] text-muted-foreground">Needs Attention</span>
          </div>
        </div>

        <div className="rounded-xl border border-border bg-card/60 p-4 backdrop-blur-sm">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-muted-foreground">Overdue Plans</span>
            <Clock className="h-4 w-4 text-rose-500" />
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-2xl font-bold text-foreground">{metrics.overduePlans}</span>
            <span className="text-[11px] text-muted-foreground">Past Target</span>
          </div>
        </div>
      </div>

      {/* Filter and Tab Controls */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 pt-2">
        {/* Tabs */}
        <div className="inline-flex rounded-xl border border-border bg-muted/40 p-1 self-start">
          <button
            onClick={() => setActiveTab('active')}
            className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-all ${
              activeTab === 'active'
                ? 'bg-card text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Active Plans
          </button>
          <button
            onClick={() => setActiveTab('archived')}
            className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-all ${
              activeTab === 'archived'
                ? 'bg-card text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Archived
          </button>
        </div>

        {/* Search & Priority */}
        <div className="flex flex-wrap sm:flex-nowrap items-center gap-2">
          <div className="relative flex-1 sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search plans or goals..."
              className="w-full rounded-xl border border-input bg-card pl-9 pr-3 py-1.5 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              helpLabel="Search records"
            />
          </div>

          <Select
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
            className="rounded-xl border border-input bg-card px-3 py-1.5 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            helpLabel="Filter priority"
          >
            <option value="all">All Priorities</option>
            <option value="urgent">Urgent</option>
            <option value="high">High</option>
            <option value="normal">Normal</option>
            <option value="low">Low</option>
          </Select>
        </div>
      </div>

      {/* Plans Grid */}
      {isError ? (
        <ErrorState
          title="Plans could not be loaded"
          message={error instanceof Error ? error.message : 'Try again.'}
          onRetry={() => refetch()}
        />
      ) : isLoading ? (
        <div className="py-20 text-center text-sm text-muted-foreground">Loading plans...</div>
      ) : plans.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card/40 p-12 text-center">
          <Layers className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
          <h3 className="text-base font-semibold text-foreground">No plans found</h3>
          <p className="text-xs text-muted-foreground mt-1 max-w-sm mx-auto">
            {search || priorityFilter !== 'all'
              ? 'Try modifying your search or filters to locate what you need.'
              : 'Create your first organizational plan to track projects and delegate tasks.'}
          </p>
          {canManage && (
            <button
              onClick={() => {
                setEditingPlan(null);
                setIsModalOpen(true);
              }}
              className="mt-4 inline-flex items-center gap-1.5 rounded-xl bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground shadow hover:bg-primary/90 transition-all"
            >
              <Plus className="h-3.5 w-3.5" />
              <span>Create Plan</span>
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {plans.map((plan) => {
            const priorityStyles =
              {
                urgent: 'bg-rose-500/10 text-rose-500 border-rose-500/20',
                high: 'bg-amber-500/10 text-amber-500 border-amber-500/20',
                normal: 'bg-primary/10 text-primary border-primary/20',
                low: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
              }[plan.priority] || 'bg-muted text-muted-foreground border-border';

            return (
              <div
                key={plan.id}
                onClick={() => navigate(`/plans/${plan.id}`)}
                role="link"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.target === event.currentTarget && event.key === 'Enter')
                    navigate(`/plans/${plan.id}`);
                }}
                className="group relative flex flex-col justify-between rounded-2xl border border-border bg-card/70 p-5 backdrop-blur-sm hover:border-primary/50 hover:shadow-xl transition-all cursor-pointer"
              >
                <div>
                  {/* Card Header */}
                  <div className="flex items-start justify-between gap-2">
                    <span
                      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${priorityStyles}`}
                    >
                      {plan.priority}
                    </span>

                    <div className="flex items-center gap-1.5">
                      {plan.is_overdue && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-rose-500/15 border border-rose-500/30 px-2 py-0.5 text-[10px] font-semibold text-rose-500">
                          <Clock className="h-2.5 w-2.5" /> Overdue
                        </span>
                      )}

                      {canManage && (
                        <div className="flex items-center gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100 transition-opacity">
                          <button
                            onClick={(e) => handleEdit(plan, e)}
                            className="rounded-lg p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                            title="Edit Plan"
                          >
                            <Edit2 className="h-3.5 w-3.5" />
                          </button>
                          <button
                            onClick={(e) => handleArchiveToggle(plan, e)}
                            disabled={archivePlan.isPending}
                            className="rounded-lg p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                            title={plan.is_archived ? 'Unarchive' : 'Archive'}
                          >
                            <Archive className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Title & Goal */}
                  <h3 className="mt-3 text-base font-bold text-foreground group-hover:text-primary transition-colors line-clamp-1">
                    {plan.title}
                  </h3>

                  {plan.goal && (
                    <div className="mt-2 flex items-start gap-1.5 rounded-lg bg-muted/40 p-2 text-xs text-muted-foreground">
                      <Target className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" />
                      <p className="line-clamp-2 leading-relaxed">{plan.goal}</p>
                    </div>
                  )}

                  {plan.description && !plan.goal && (
                    <p className="mt-2 text-xs text-muted-foreground line-clamp-2 leading-relaxed">
                      {plan.description}
                    </p>
                  )}
                </div>

                <div className="mt-5 space-y-3 pt-3 border-t border-border/60">
                  {/* Progress Bar */}
                  <div>
                    <div className="flex items-center justify-between text-xs mb-1.5">
                      <span className="font-semibold text-foreground">{plan.progress_pct}%</span>
                      <span className="text-[11px] text-muted-foreground">
                        {plan.completed_tasks}/{plan.total_tasks} completed
                      </span>
                    </div>
                    <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-300 ${
                          plan.progress_pct === 100
                            ? 'bg-emerald-500'
                            : plan.blocked_tasks > 0
                              ? 'bg-amber-500'
                              : 'bg-primary'
                        }`}
                        style={{ width: `${plan.progress_pct}%` }}
                      />
                    </div>
                  </div>

                  {/* Footer Info */}
                  <div className="flex items-center justify-between text-[11px] text-muted-foreground pt-1">
                    <div className="flex items-center gap-1.5">
                      <Calendar className="h-3.5 w-3.5" />
                      <span>
                        {plan.target_date
                          ? parseCalendarDate(plan.target_date)?.toLocaleDateString(undefined, {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            })
                          : 'No target date'}
                      </span>
                    </div>

                    <div className="flex items-center gap-1 text-primary font-semibold group-hover:translate-x-0.5 transition-transform">
                      <span>View Tasks</span>
                      <ArrowUpRight className="h-3.5 w-3.5" />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Plan Form Modal */}
      <PlanFormModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingPlan(null);
        }}
        planToEdit={editingPlan}
      />
    </div>
  );
}
